// Constants
import { TenantRegion } from "@ms/uno-constants/lib/local/AppConstants";
import { DataPreFetchFailureStatusForMru, DataPreFetchFailureStatusForView, PreFetchedDataKeys, } from "@ms/uno-constants/lib/local/DataPreFetcherConstants";
import { CurrentPlannerServiceODataVersion, HttpContentType, HttpHeaders, HttpMethods, TrackChangesHeaderValue, } from "@ms/uno-constants/lib/local/HttpConstants";
import { RequestHeaders as ServiceRequestHeaders } from "@ms/uno-constants/lib/local/ServiceConstants";
import { generateGuid } from "@ms/uno-utilities/lib/local/Guid";
import { MaxMruCount, MruHeaders } from "@ms/uno-services/lib/local/services/mru/MruConstants";
import { RequestHeaders, ResponseHeaders } from "@ms/uno-services/lib/local/services/planner/Headers";
import { fetchWithTimeout, generateAjaxClientError, generateLogStringFromAjaxClientError, getAppsEnabledInEnvironment, } from "@ms/uno-services/lib/local/utilities/ServiceUtilities";
/**
 * Pre-fetcher services
 */
export class PreFetcherServices {
    /**
     * Constructor for the PreFetcherServices
     * @param configProvider The configuration provider for feature flags, settings, and session metadata
     * @param tokenProvider The token provider
     * @param dataPreFetcherActionCreator The data pre-fetch action creator to store the pre-fetched data
     */
    constructor(configProvider, tokenProvider, dataPreFetcherActionCreator) {
        this.configProvider = configProvider;
        this.tokenProvider = tokenProvider;
        this.dataPreFetcherActionCreator = dataPreFetcherActionCreator;
        this.version = "v4.0";
        this.endpointPrefix = "taskapi";
        this.taskExpansionFields = ["checklist", "links", "userAssignments"];
        this.planExpansionFields = ["planView/categories", "links", "contexts"];
        const plannerServiceConfig = this.configProvider().settings.serviceConfigurations.planner;
        this.plannerBaseUrl = `${plannerServiceConfig.hostname}/${this.endpointPrefix}/${this.version}`;
    }
    /**
     * Fetches the MRU data
     */
    async fetchMruData() {
        let result;
        try {
            const { hostname, hostnameEu, authResourceURI } = this.configProvider().settings.serviceConfigurations.mru;
            const baseUrl = this.configProvider().sessionMetaData.tenantRegion === TenantRegion.Row ? hostname : hostnameEu;
            if (!baseUrl) {
                throw generateAjaxClientError(null, "Base URL is required for MRU service");
            }
            const apiUrl = `${baseUrl}/ocs/v2/recent?show=${MaxMruCount}${`&apps=${getAppsEnabledInEnvironment(this.configProvider().settings.EnvironmentFeatureSettings ?? {}).join(",")}`}${"&cap=DocumentUrl&sort=date"}`;
            const { response } = await this.executeRequest(apiUrl, {
                method: HttpMethods.Get,
                headers: {
                    ...this.getMruHeaders(),
                    [HttpHeaders.ContentType]: HttpContentType.Json,
                },
            }, authResourceURI, false);
            const data = await response.clone().json();
            if (!data) {
                throw generateAjaxClientError(null, new Error("No data returned"));
            }
            const mruResources = [...(data.documents?.items || []), ...(data.places?.items || [])];
            result = {
                type: PreFetchedDataKeys.Mru,
                data: mruResources,
            };
        }
        catch (error) {
            const failedResult = error;
            const errorMessage = generateLogStringFromAjaxClientError(failedResult);
            result = {
                type: PreFetchedDataKeys.Mru,
                data: [],
                errorMessage: errorMessage,
            };
            window[DataPreFetchFailureStatusForMru] = true;
        }
        this.dataPreFetcherActionCreator.storePrefetchedServiceResult(result);
    }
    /**
     * Fetches the planner my day tasks data
     * Fetching all pages instead of just the first page because the next link is not stored.
     * Therefore, we can't dedupe only the first page call.
     * @param nextLink The next link
     * @param trouterViewUpdateKey The Trouter subscription result from first page (passed through pagination)
     */
    async fetchPlannerMyDayTasksData(nextLink, trouterViewUpdateKey) {
        try {
            const url = this.getEndpointUrl(`${this.plannerBaseUrl}/users('me')/myDayTasks?$expand=${this.taskExpansionFields.join(",")}`, nextLink);
            const requestResult = await this.executeRequest(url, {
                method: HttpMethods.Get,
                headers: {
                    [HttpHeaders.PreferHeader]: TrackChangesHeaderValue,
                },
            }, this.configProvider().settings.serviceConfigurations.planner.authResourceURI, true);
            const data = await requestResult.response.clone().json();
            if (!data) {
                throw generateAjaxClientError(null, new Error("No data returned"));
            }
            // Capture Trouter subscription result from first page only
            const subscriptionKey = trouterViewUpdateKey ?? requestResult.trouterViewUpdateKey;
            const nextLinkInResponse = data["@nextLink"];
            const deltaLinkInResponse = data["@deltaLink"];
            const tasks = data.value;
            const result = {
                type: PreFetchedDataKeys.MyDayTasks,
                data: tasks,
                nextLink: nextLinkInResponse,
                deltaLink: deltaLinkInResponse,
                trouterViewUpdateKey: subscriptionKey,
            };
            this.dataPreFetcherActionCreator.storePrefetchedServiceResult(result);
            // If there is a next link, fetch the next page of tasks
            if (nextLinkInResponse) {
                await this.fetchPlannerMyDayTasksData(nextLinkInResponse, subscriptionKey);
            }
        }
        catch (error) {
            const failedResult = error;
            const errorMessage = generateLogStringFromAjaxClientError(failedResult);
            const result = {
                type: PreFetchedDataKeys.MyDayTasks,
                data: [],
                errorMessage: errorMessage,
            };
            this.dataPreFetcherActionCreator.storePrefetchedServiceResult(result);
            window[DataPreFetchFailureStatusForView] = true;
        }
    }
    /**
     * Fetches the planner assigned to me tasks data.
     * Fetching all pages instead of just the first page because the next link is not stored.
     * Therefore, we can't dedupe only the first page call.
     * @param nextLink The next link
     * @param trouterViewUpdateKey The Trouter subscription result from first page (passed through pagination)
     */
    async fetchPlannerAssignedToMeTasksData(nextLink, trouterViewUpdateKey) {
        try {
            const url = this.getEndpointUrl(`${this.plannerBaseUrl}/users('me')/tasks?$expand=${this.taskExpansionFields.join(",")}`, nextLink);
            const requestResult = await this.executeRequest(url, {
                method: HttpMethods.Get,
                headers: {
                    [HttpHeaders.PreferHeader]: TrackChangesHeaderValue,
                },
            }, this.configProvider().settings.serviceConfigurations.planner.authResourceURI, true);
            const tasksPage = await requestResult.response.clone().json();
            if (!tasksPage) {
                throw generateAjaxClientError(null, new Error("No data returned"));
            }
            // Capture Trouter subscription result from first page only
            const subscriptionKey = trouterViewUpdateKey ?? requestResult.trouterViewUpdateKey;
            const nextLinkInResponse = tasksPage["@nextLink"];
            const deltaLinkInResponse = tasksPage["@deltaLink"];
            const atmTasks = tasksPage.value;
            const result = {
                type: PreFetchedDataKeys.ATMTasks,
                data: atmTasks,
                nextLink: nextLinkInResponse,
                deltaLink: deltaLinkInResponse,
                trouterViewUpdateKey: subscriptionKey,
            };
            // Store the service result without wait for the plan fetch to complete
            this.dataPreFetcherActionCreator.storePrefetchedServiceResult(result);
            // If there is a next link, fetch the next page of tasks
            if (nextLinkInResponse) {
                await this.fetchPlannerAssignedToMeTasksData(nextLinkInResponse, subscriptionKey);
            }
        }
        catch (error) {
            const failedResult = error;
            const errorMessage = generateLogStringFromAjaxClientError(failedResult);
            const result = {
                type: PreFetchedDataKeys.ATMTasks,
                data: [],
                errorMessage: errorMessage,
            };
            this.dataPreFetcherActionCreator.storePrefetchedServiceResult(result);
            window[DataPreFetchFailureStatusForView] = true;
        }
    }
    /**
     * Fetches the teams tasks tabs data
     */
    async fetchTTTabsData() {
        let result;
        try {
            const retailServicesConfig = this.configProvider().settings.serviceConfigurations.retailservices;
            const baseUrl = retailServicesConfig.hostname;
            const appFlavor = retailServicesConfig.appFlavor;
            if (!appFlavor) {
                throw new Error("Client flavor is required for TeamsTasksTabs service");
            }
            const apiUrl = `${baseUrl}/api/rest/v2/teams/tabs/delta?env=${appFlavor}`;
            const { response } = await this.executeRequest(apiUrl, {
                method: HttpMethods.Get,
            }, retailServicesConfig.authResourceURI, false);
            const data = await response.clone().json();
            if (!data) {
                throw generateAjaxClientError(null, new Error("No data returned"));
            }
            result = {
                type: PreFetchedDataKeys.TTTabs,
                data: data.items ?? [],
            };
        }
        catch (error) {
            const failedResult = error;
            const errorMessage = generateLogStringFromAjaxClientError(failedResult);
            result = {
                type: PreFetchedDataKeys.TTTabs,
                data: [],
                errorMessage: errorMessage,
            };
            window[DataPreFetchFailureStatusForView] = true;
        }
        this.dataPreFetcherActionCreator.storePrefetchedServiceResult(result);
    }
    /**
     * Fetches the plan data
     * @param planId The plan id to fetch
     */
    async fetchPlan(planId) {
        let result;
        try {
            const url = `${this.plannerBaseUrl}/plans('${planId}')?$expand=${this.planExpansionFields.join(",")}`;
            const { response, trouterViewUpdateKey } = await this.executeRequest(url, {
                method: HttpMethods.Get,
                headers: {
                    Prefer: "include-unknown-enum-members",
                },
            }, this.configProvider().settings.serviceConfigurations.planner.authResourceURI, true);
            const data = await response.clone().json();
            if (!data) {
                throw generateAjaxClientError(null, new Error("No data returned"));
            }
            result = {
                type: PreFetchedDataKeys.Plan,
                data: [data],
                trouterViewUpdateKey,
            };
        }
        catch (error) {
            const failedResult = error;
            const errorMessage = generateLogStringFromAjaxClientError(failedResult);
            result = {
                type: PreFetchedDataKeys.Plan,
                data: [],
                errorMessage: errorMessage,
            };
            window[DataPreFetchFailureStatusForView] = true;
        }
        this.dataPreFetcherActionCreator.storePrefetchedServiceResult(result);
    }
    /**
     * Fetches all buckets for a plan
     * @param planId The plan id to fetch buckets for
     * @param nextLink The next link
     * @param trouterViewUpdateKey The Trouter subscription result from first page (passed through pagination)
     */
    async fetchBuckets(planId, nextLink, trouterViewUpdateKey) {
        try {
            const url = this.getEndpointUrl(`${this.plannerBaseUrl}/plans('${planId}')/buckets`, nextLink);
            const requestResult = await this.executeRequest(url, {
                method: HttpMethods.Get,
                headers: {
                    [HttpHeaders.PreferHeader]: TrackChangesHeaderValue,
                },
            }, this.configProvider().settings.serviceConfigurations.planner.authResourceURI, true);
            const data = await requestResult.response.clone().json();
            if (!data) {
                throw generateAjaxClientError(null, new Error("No data returned"));
            }
            // Capture Trouter subscription result from first page only
            const subscriptionKey = trouterViewUpdateKey ?? requestResult.trouterViewUpdateKey;
            const nextLinkInResponse = data["@nextLink"];
            const deltaLinkInResponse = data["@deltaLink"];
            const bucketResources = data.value;
            const result = {
                type: PreFetchedDataKeys.Buckets,
                planId: planId,
                data: bucketResources,
                nextLink: nextLinkInResponse,
                deltaLink: deltaLinkInResponse,
                trouterViewUpdateKey: subscriptionKey,
            };
            this.dataPreFetcherActionCreator.storePrefetchedServiceResult(result);
            // If there is a next link, fetch the next page of buckets
            if (nextLinkInResponse) {
                await this.fetchBuckets(planId, nextLinkInResponse, subscriptionKey);
            }
        }
        catch (error) {
            const failedResult = error;
            const errorMessage = generateLogStringFromAjaxClientError(failedResult);
            const result = {
                type: PreFetchedDataKeys.Buckets,
                planId: planId,
                data: [],
                errorMessage: errorMessage,
            };
            this.dataPreFetcherActionCreator.storePrefetchedServiceResult(result);
            window[DataPreFetchFailureStatusForView] = true;
        }
    }
    /**
     * Fetches todo settings
     */
    async fetchTodoSettings() {
        let result;
        try {
            const stapiServiceConfig = this.configProvider().settings.serviceConfigurations.stapi;
            const baseUrl = `${stapiServiceConfig.hostname}/todob2`;
            const apiUrl = `${baseUrl}/api/v1/settings`;
            const { response } = await this.executeRequest(apiUrl, {
                method: HttpMethods.Get,
                headers: {
                    [HttpHeaders.ContentType]: HttpContentType.Json,
                },
            }, stapiServiceConfig.authResourceURI, false);
            const data = await response.clone().json();
            if (!data) {
                throw generateAjaxClientError(null, new Error("No data returned"));
            }
            const todoSettingsResource = data.Value;
            result = {
                type: PreFetchedDataKeys.TodoSettings,
                data: todoSettingsResource,
            };
        }
        catch (error) {
            const failedResult = error;
            const errorMessage = generateLogStringFromAjaxClientError(failedResult);
            result = {
                type: PreFetchedDataKeys.TodoSettings,
                data: [],
                errorMessage: errorMessage,
            };
            window[DataPreFetchFailureStatusForView] = true;
        }
        this.dataPreFetcherActionCreator.storePrefetchedServiceResult(result);
    }
    /**
     * Fetches all tasks for a basic plan
     * Fetching all pages instead of just the first page because the next link is not stored.
     * Therefore, we can't dedupe only the first page call, fetching tasks will short circuit via makeRequestToStore.
     * @param planId The plan id to fetch tasks for
     * @param nextLink The next link
     * @param trouterViewUpdateKey The Trouter subscription result from first page (passed through pagination)
     */
    async fetchTasksForPlan(planId, nextLink, trouterViewUpdateKey) {
        try {
            const url = this.getEndpointUrl(`${this.plannerBaseUrl}/plans('${planId}')/tasks?$expand=${this.taskExpansionFields.join(",")}`, nextLink);
            const requestResult = await this.executeRequest(url, {
                method: HttpMethods.Get,
                headers: {
                    [HttpHeaders.PreferHeader]: TrackChangesHeaderValue,
                },
            }, this.configProvider().settings.serviceConfigurations.planner.authResourceURI, true);
            const tasksPage = await requestResult.response.clone().json();
            if (!tasksPage) {
                throw generateAjaxClientError(null, new Error("No data returned"));
            }
            // Capture Trouter subscription result from first page only
            const subscriptionKey = trouterViewUpdateKey ?? requestResult.trouterViewUpdateKey;
            const nextLinkInResponse = tasksPage["@nextLink"];
            const deltaLinkInResponse = tasksPage["@deltaLink"];
            const tasks = tasksPage.value;
            const result = {
                type: PreFetchedDataKeys.BasicPlanTasks,
                planId: planId,
                data: tasks,
                nextLink: nextLinkInResponse,
                deltaLink: deltaLinkInResponse,
                trouterViewUpdateKey: subscriptionKey,
            };
            this.dataPreFetcherActionCreator.storePrefetchedServiceResult(result);
            // If there is a next link, fetch the next page of tasks
            if (nextLinkInResponse) {
                await this.fetchTasksForPlan(planId, nextLinkInResponse, subscriptionKey);
            }
        }
        catch (error) {
            const failedResult = error;
            const errorMessage = generateLogStringFromAjaxClientError(failedResult);
            const result = {
                type: PreFetchedDataKeys.BasicPlanTasks,
                planId: planId,
                data: [],
                errorMessage: errorMessage,
            };
            this.dataPreFetcherActionCreator.storePrefetchedServiceResult(result);
            window[DataPreFetchFailureStatusForView] = true;
        }
    }
    /**
     * Fetches all custom field definitions for a plan.
     * Custom field definitions are guaranteed to fit in a single response (no pagination).
     * @param planId The plan id to fetch custom field definitions for
     */
    async fetchCustomFieldDefinitions(planId) {
        try {
            const url = `${this.plannerBaseUrl}/plans('${planId}')/customFieldDefinitions?$expand=choiceOptions`;
            const requestResult = await this.executeRequest(url, {
                method: HttpMethods.Get,
                headers: {
                    [HttpHeaders.PreferHeader]: TrackChangesHeaderValue,
                },
            }, this.configProvider().settings.serviceConfigurations.planner.authResourceURI, true);
            const data = await requestResult.response.clone().json();
            if (!data) {
                throw generateAjaxClientError(null, new Error("No data returned"));
            }
            const deltaLinkInResponse = data["@deltaLink"];
            const customFieldDefinitionResources = data.value;
            const result = {
                type: PreFetchedDataKeys.CustomFieldDefinitions,
                planId: planId,
                data: customFieldDefinitionResources,
                deltaLink: deltaLinkInResponse,
                trouterViewUpdateKey: requestResult.trouterViewUpdateKey,
            };
            this.dataPreFetcherActionCreator.storePrefetchedServiceResult(result);
        }
        catch (error) {
            const failedResult = error;
            const errorMessage = generateLogStringFromAjaxClientError(failedResult);
            const result = {
                type: PreFetchedDataKeys.CustomFieldDefinitions,
                planId: planId,
                data: [],
                errorMessage: errorMessage,
            };
            this.dataPreFetcherActionCreator.storePrefetchedServiceResult(result);
            window[DataPreFetchFailureStatusForView] = true;
        }
    }
    /**
     * Fetches a single task by id and stores the result via the action creator. When the fetched
     * task reports `hasChat = true`, this method also kicks off fetch of the task's chat messages
     * @param taskId The id of the task to fetch
     */
    async fetchTask(taskId) {
        try {
            const url = `${this.plannerBaseUrl}/tasks('${taskId}')?$expand=${this.taskExpansionFields.join(",")}`;
            const { response, trouterViewUpdateKey } = await this.executeRequest(url, {
                method: HttpMethods.Get,
            }, this.configProvider().settings.serviceConfigurations.planner.authResourceURI, true);
            const data = await response.clone().json();
            if (!data) {
                throw generateAjaxClientError(null, new Error("No data returned"));
            }
            const result = {
                type: PreFetchedDataKeys.Task,
                taskId: taskId,
                data: data,
                trouterViewUpdateKey,
            };
            this.dataPreFetcherActionCreator.storePrefetchedServiceResult(result);
            // Only fetch messages when we know the task has a chat thread
            if (data.hasChat) {
                await this.fetchTaskMessages(taskId);
            }
        }
        catch (error) {
            const failedResult = error;
            const errorMessage = generateLogStringFromAjaxClientError(failedResult);
            const result = {
                type: PreFetchedDataKeys.Task,
                taskId: taskId,
                data: null,
                errorMessage: errorMessage,
            };
            this.dataPreFetcherActionCreator.storePrefetchedServiceResult(result);
            window[DataPreFetchFailureStatusForView] = true;
        }
    }
    /**
     * Fetches the first page of chat messages for a task and dispatches the result.
     *
     * Chat differs from other Planner collections in that the messages endpoint never returns an
     * `@deltaLink`, and `@nextLink` is *always* present (even at end-of-stream); the action creator
     * synthesizes the delta-sync URL client-side.
     * @param taskId The id of the task whose messages to fetch
     */
    async fetchTaskMessages(taskId) {
        try {
            const url = `${this.plannerBaseUrl}/tasks('${taskId}')/messages`;
            const requestResult = await this.executeRequest(url, {
                method: HttpMethods.Get,
                headers: {
                    [HttpHeaders.PreferHeader]: TrackChangesHeaderValue,
                },
            }, this.configProvider().settings.serviceConfigurations.planner.authResourceURI, true);
            const data = await requestResult.response.clone().json();
            if (!data) {
                throw generateAjaxClientError(null, new Error("No data returned"));
            }
            const result = {
                type: PreFetchedDataKeys.TaskMessages,
                taskId: taskId,
                data: (data.value ?? []),
                nextLink: data["@nextLink"],
                trouterViewUpdateKey: requestResult.trouterViewUpdateKey,
            };
            this.dataPreFetcherActionCreator.storePrefetchedServiceResult(result);
        }
        catch (error) {
            const failedResult = error;
            const errorResult = {
                type: PreFetchedDataKeys.TaskMessages,
                taskId: taskId,
                data: [],
                errorMessage: generateLogStringFromAjaxClientError(failedResult),
            };
            this.dataPreFetcherActionCreator.storePrefetchedServiceResult(errorResult);
            window[DataPreFetchFailureStatusForView] = true;
        }
    }
    /**
     * Get the graph token from the retail service
     */
    async getGraphTokenFromRetailService() {
        const retailServiceConfig = this.configProvider().settings.serviceConfigurations.auth;
        const baseUrl = retailServiceConfig.hostname;
        const apiUrl = `${baseUrl}/api/rest/v1/tokenprovider/graph`;
        const { response } = await this.executeRequest(apiUrl, {
            method: HttpMethods.Get,
        }, retailServiceConfig.authResourceURI, false);
        const data = await response.clone().json();
        return data.accessToken;
    }
    /**
     * Sets common headers required by MRU service.
     */
    getMruHeaders() {
        const headers = {};
        const officeApplicationNumber = this.configProvider().settings.serviceConfigurations.mru.officeApplicationNumber;
        if (!officeApplicationNumber) {
            throw new Error("Office application number is required for MRU service");
        }
        // These are REQUIRED properties for MRU and it will stop working without these
        headers[MruHeaders.OfficePlatform] = "Web";
        headers[MruHeaders.OfficeApplication] = officeApplicationNumber;
        headers[MruHeaders.OfficeVersion] = "16.0.0.0";
        return headers;
    }
    /**
     * Execute an request
     * @param url Resource url for the request
     * @param options Options to configure the request
     * @param authResourceUri The AAD/MSA auth resource URI to acquire a token for
     * @param isPlannerService Whether the request targets the planner service. Controls planner-specific OData/Trouter headers.
     * @param timeout Timeout for the request
     */
    async executeRequest(url, options, authResourceUri, isPlannerService, timeout) {
        const token = await this.tokenProvider(authResourceUri);
        // Set up headers similar to PlannerService
        const headers = new Headers(options.headers);
        // Set accept header if not already set
        if (!headers.has(HttpHeaders.Accept)) {
            headers.set(HttpHeaders.Accept, "application/json, */*;q=0.01");
        }
        // Set OData version header for planner service
        if (isPlannerService) {
            headers.set(HttpHeaders.ODataVersion, CurrentPlannerServiceODataVersion);
            // Set Trouter subscription endpoint header if enabled
            const trouterEndpointId = this.getTrouterEndpointId();
            if (trouterEndpointId) {
                headers.set(RequestHeaders.TrouterSubscriptionEndpoint, trouterEndpointId);
            }
            // Set X-CorrelationId so prefetch traffic matches the decorated PlannerService stack
            if (!this.configProvider().changeGates.DisablePlannerXCorrelationId) {
                headers.set(ServiceRequestHeaders.XCorrelationId, generateGuid());
            }
        }
        // Set authorization header
        headers.set(HttpHeaders.Authorization, `Bearer ${token}`);
        // Update options with headers
        const requestOptions = {
            ...options,
            headers,
        };
        try {
            const response = await fetchWithTimeout(url, requestOptions, timeout ?? 15000);
            if (response.ok) {
                // Capture Trouter subscription result header for planner service
                const trouterViewUpdateKey = isPlannerService
                    ? response.headers?.get(ResponseHeaders.TrouterViewUpdateKey) ?? undefined
                    : undefined;
                return { response, trouterViewUpdateKey };
            }
            // Failure
            const failureResult = {
                response: response,
                error: null,
            };
            throw failureResult;
        }
        catch (e) {
            if (e.response != null) {
                // This was already thrown above as an IFailedRequestResult. No need to wrap again
                throw e;
            }
            // If no response returned
            const failureResult = {
                response: null,
                error: e.name === "AbortError" ? new Error("Timeout") : e,
            };
            throw failureResult;
        }
    }
    /**
     * Get the endpoint URL, preferring nextUrl if provided (similar to PlannerService)
     * @param defaultEndpoint The default endpoint URL
     * @param nextUrl The next URL from pagination
     */
    getEndpointUrl(defaultEndpoint, nextUrl) {
        return nextUrl && nextUrl.trim() !== "" ? nextUrl : defaultEndpoint;
    }
    /**
     * Gets the Trouter endpoint ID if Trouter is enabled.
     * Returns null if Trouter is disabled via flight or settings.
     */
    getTrouterEndpointId() {
        // Check if Trouter is enabled via flight
        if (!this.configProvider().flights.EnableTrouterIntegration) {
            return null;
        }
        // Check if Trouter is disabled via settings
        if (this.configProvider().settings.trouterSettings.disable) {
            return null;
        }
        // Return session ID as the endpoint ID
        return this.configProvider().sessionMetaData.sessionId;
    }
}
